import { useEffect, useCallback } from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import style from "./AdPhaFrameTester.module.scss";

const ADPHA_TEST_LINKS = [
  {
    id: "drtc",
    label: "DRTC monograph",
    url: "https://dev.drtc.tg.org.au/monograph/paracetamol",
  },
  {
    id: "obesid",
    label: "ObesiD monograph",
    url: "https://ippontest.ob.tg.org.au/monograph/paracetamol",
  },
  {
    id: "aidh",
    label: "AIDH monograph",
    url: "https://dev.aidh.tg.org.au/monograph/paracetamol",
  },
];

const isAllowedTesterEnvironment = () => {
  const envName = (import.meta.env.VITE_ENV_NAME || import.meta.env.MODE || "").toLowerCase();
  const hostname = typeof window !== "undefined" ? window.location.hostname.toLowerCase() : "";
  const isStagingHost = hostname.includes("stage.") || hostname.includes("staging.");

  return (
    import.meta.env.DEV ||
    import.meta.env.MODE === "test" ||
    envName === "stage" ||
    envName === "staging" ||
    isStagingHost
  );
};

const AdPhaFrameTester = ({
  splitPaneEnabled = false,
  setSplitPaneEnabled = () => {},
  activeLink = null,
  setActiveLink = () => {},
}) => {
  const isTesterEnabled =
    import.meta.env.VITE_ADPHA_FRAME_TESTER_ENABLED === "true" && isAllowedTesterEnvironment();

  const closeFrame = useCallback(() => {
    setActiveLink(null);
  }, [setActiveLink]);

  const openInNewTab = useCallback(() => {
    if (!activeLink?.url) return;
    window.open(activeLink.url, "_blank", "noopener,noreferrer");
  }, [activeLink]);

  useEffect(() => {
    if (!isTesterEnabled || !activeLink || splitPaneEnabled) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        closeFrame();
      }
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isTesterEnabled, activeLink, splitPaneEnabled, closeFrame]);

  if (!isTesterEnabled) {
    return null;
  }

  return (
    <section className={style.testerSection} aria-label="AdPha frame tester">
      <h3 className={classNames("size-16", style.testerTitle)}>AdPha Frame Tester</h3>
      <div className={style.testerLinks}>
        {ADPHA_TEST_LINKS.map((link) => (
          <button
            key={link.id}
            type="button"
            className={style.testerLink}
            onClick={() => setActiveLink(link)}
          >
            {link.label}
          </button>
        ))}
      </div>

      <div className={style.testerControls}>
        <label className={style.splitPaneToggle}>
          <input
            type="checkbox"
            checked={splitPaneEnabled}
            onChange={(e) => setSplitPaneEnabled(e.target.checked)}
          />
          Split pane
        </label>
      </div>

      {!splitPaneEnabled && activeLink && (
        <div className={style.embedBackdrop} onClick={closeFrame}>
          <aside
            className={style.embedSheet}
            role="dialog"
            aria-modal="true"
            aria-label={`${activeLink.label} iframe`}
            onClick={(event) => event.stopPropagation()}
          >
            <header className={style.embedHeader}>
              <h2>{activeLink.label}</h2>
              <div className={style.embedActions}>
                <button type="button" onClick={openInNewTab}>
                  Open In New Tab
                </button>
                <button type="button" onClick={closeFrame} aria-label="Close tester frame">
                  Close
                </button>
              </div>
            </header>

            <div className={style.embedFrameWrapper}>
              <iframe
                src={activeLink.url}
                title={`${activeLink.label} embedded`}
                className={style.embedIframe}
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
                sandbox="allow-forms allow-modals allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation allow-top-navigation-by-user-activation"
              ></iframe>
            </div>
          </aside>
        </div>
      )}
    </section>
  );
};

AdPhaFrameTester.propTypes = {
  splitPaneEnabled: PropTypes.bool,
  setSplitPaneEnabled: PropTypes.func,
  activeLink: PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    url: PropTypes.string,
  }),
  setActiveLink: PropTypes.func,
};

export default AdPhaFrameTester;
